import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, MenuItem, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

const partsFromValue = value => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
    return match ? { year: match[1], month: match[2], day: match[3] } : { year: '', month: '', day: '' };
};

export const dateOfBirthValidation = (value, today = new Date()) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
    if (!match) return 'invalid';
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const candidate = new Date(Date.UTC(year, month - 1, day));
    if (candidate.getUTCFullYear() !== year || candidate.getUTCMonth() !== month - 1 || candidate.getUTCDate() !== day) return 'invalid';
    const current = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
    if (candidate > current) return 'invalid';
    let age = current.getUTCFullYear() - year;
    if (current.getUTCMonth() < month - 1 || (current.getUTCMonth() === month - 1 && current.getUTCDate() < day)) age--;
    return age >= 18 ? 'valid' : 'underage';
};

export default function DateOfBirthField({ value, onChange, error = false, helperText = '' }) {
    const { t } = useTranslation();
    const initial = partsFromValue(value);
    const [year, setYear] = useState(initial.year);
    const [month, setMonth] = useState(initial.month);
    const [day, setDay] = useState(initial.day);
    const currentYear = new Date().getFullYear();
    const years = useMemo(() => Array.from({ length: 121 }, (_, index) => String(currentYear - index)), [currentYear]);
    const daysInMonth = /^\d{4}$/.test(year) && month
        ? new Date(Number(year), Number(month), 0).getDate()
        : 31;

    useEffect(() => {
        if (Number(day) > daysInMonth) setDay('');
    }, [day, daysInMonth]);

    useEffect(() => {
        const complete = /^\d{4}$/.test(year) && month && day;
        onChange(complete ? `${year}-${month}-${day}` : '');
    }, [year, month, day, onChange]);

    return <Box className="date-of-birth-field" role="group" aria-label={t('auth.dateOfBirth')} sx={{ my: 1.5 }}>
        <Typography component="div" className="date-of-birth-label" sx={{ mb: .75, color: error ? 'error.main' : 'text.secondary' }}>
            {t('auth.dateOfBirth')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(7rem, 1.25fr) repeat(2, minmax(5rem, 1fr))', gap: 1 }}>
            <Autocomplete
                freeSolo
                autoSelect
                options={years}
                value={year || null}
                inputValue={year}
                onInputChange={(_, next) => setYear(next.replace(/\D/g, '').slice(0, 4))}
                onChange={(_, next) => setYear(String(next || '').replace(/\D/g, '').slice(0, 4))}
                renderInput={params => <TextField {...params} label={t('auth.year')} error={error} inputProps={{ ...params.inputProps, inputMode: 'numeric', maxLength: 4 }} />}
            />
            <TextField select label={t('auth.month')} value={month} error={error} onChange={event => setMonth(event.target.value)}>
                {Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, '0')).map(item =>
                    <MenuItem key={item} value={item}>{Number(item)}</MenuItem>)}
            </TextField>
            <TextField select label={t('auth.day')} value={day} error={error} onChange={event => setDay(event.target.value)}>
                {Array.from({ length: daysInMonth }, (_, index) => String(index + 1).padStart(2, '0')).map(item =>
                    <MenuItem key={item} value={item}>{Number(item)}</MenuItem>)}
            </TextField>
        </Box>
        {helperText && <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ display: 'block', mt: .5, ml: 1.75 }}>
            {helperText}
        </Typography>}
    </Box>;
}
